// @ts-nocheck

import {
    ArrowOutwardIcon,
    DoneIcon,
    ErrorOutlineOutlinedIcon,
    LayersOutlinedIcon,
    LocalOfferOutlinedIcon,
    MoneyOffIcon,
    MoreHorizIcon,
    PowerSettingsNewOutlinedIcon,
    WarningAmberIcon,
} from '@ds/coolshop';
import { ModelGlStatus } from '@src/__generated__/types';
import { useGetModelGlsStatusesQuery } from '@src/modules/prototype/graphql/prototype.generated';
import { ReactElement, useMemo } from 'react';
import { useIntl } from 'react-intl';

export type GlStatuses = {
    glId: string;
    hasCompatiblePriceLists: boolean;
    statuses: Array<{
        name: string;
        icon: ReactElement;
        status: ModelGlStatus;
    }>;
};

export const useGlStatuses = (
    modelId: string = '',
    glIds: string[] = [],
): {
    glsStatuses: GlStatuses[];
    refetchStatuses: ReturnType<typeof useGetModelGlsStatusesQuery>['refetch'];
    misalignedGls: MisalignedGlData[];
    loadingStatus: boolean;
} => {
    const intl = useIntl();

    const { data, refetch, loading } = useGetModelGlsStatusesQuery({
        variables: {
            modelId,
            glIds,
        },
        fetchPolicy: 'network-only',
        skip: !modelId,
    });

    // Memoized on the query result: consumers put the statuses in column definitions, and a new
    // array on every render would rebuild the grid columns and remount every cell.
    const glsStatuses = useMemo<GlStatuses[]>(() => {
        const ICON_MAPPING: Record<ModelGlStatus, ReactElement> = {
            Active: <PowerSettingsNewOutlinedIcon color="primary" style={{ fontSize: '1rem' }} />,
            Error: <ErrorOutlineOutlinedIcon color="error" style={{ fontSize: '1rem' }} />,
            Replaced: <ErrorOutlineOutlinedIcon color="secondary" style={{ fontSize: '1rem' }} />,
            CertificationSuccess: <DoneIcon color="success" style={{ fontSize: '1rem' }} />,
            CertificationError: <WarningAmberIcon color="error" style={{ fontSize: '1rem' }} />,
            Certificating: <MoreHorizIcon color="disabled" style={{ fontSize: '1rem' }} />,
            ToCertificate: <ArrowOutwardIcon color="disabled" style={{ fontSize: '1rem' }} />,
            StdPriceDelta: <LocalOfferOutlinedIcon color="disabled" style={{ fontSize: '1rem' }} />,
            Trasversal: <LayersOutlinedIcon color="disabled" style={{ fontSize: '1rem' }} />,
            MisalignedPrice: <MoneyOffIcon color="error" style={{ fontSize: '1rem' }} />,
        };

        return (data?.modelGlsStatuses ?? []).map<GlStatuses>(gl => ({
            glId: gl.glId,
            hasCompatiblePriceLists: gl.hasCompatiblePriceLists ?? false,
            statuses: gl.statuses.map(s => {
                let name: string = s.status;

                if (s.status === ModelGlStatus.Error) {
                    name = `${intl.formatMessage({
                        id: 'disabled-items',
                        defaultMessage: 'Disabled items',
                    })}: ${s.data?.join(', ') ?? ''}`;
                }

                if (s.status === ModelGlStatus.Replaced) {
                    name = `${intl.formatMessage({
                        id: 'Replaced-items',
                        defaultMessage: 'Replaced items',
                    })}: ${s.data?.join(', ') ?? ''}`;
                }

                if (s.status === ModelGlStatus.Trasversal) {
                    name = `${intl.formatMessage({
                        id: 'linked-models',
                        defaultMessage: 'Linked models',
                    })}: ${s.data?.join(', ') ?? ''}`;
                }

                if (s.status === ModelGlStatus.StdPriceDelta) {
                    name = s.data?.join(', ') ?? '';
                }

                return {
                    name,
                    icon: ICON_MAPPING[s.status],
                    status: s.status,
                };
            }),
        }));
    }, [data, intl]);

    return {
        glsStatuses,
        misalignedGls: (data?.modelGlsStatuses ?? [])
            .filter(gl => gl.statuses.some(s => s.status === ModelGlStatus.Replaced))
            .reduce((acc, gl) => {
                acc.push({
                    hierarchyId: gl.glHierarchyId,
                    nodeId: gl.glId,
                    code: gl.glCode,
                    items: gl.statuses.find(s => s.status === ModelGlStatus.Replaced)?.items ?? [],
                });
                return acc;
            }, []) satisfies MisalignedGlData[],
        refetchStatuses: refetch,
        loadingStatus: loading,
    };
};
